/**
 * Roles allowed on each role-gated page, keyed by route slug. Single source for the
 * router guards (App.jsx) and the navigation menu (AppShell), so a menu never offers
 * a link the router would bounce to /403. The API enforces its own permissions.
 */
export const ROUTE_ROLES = {
  'role-permissions-security-policies': ['Admin'],
  'system-integrations-webhooks-monitor': ['Admin'],
  'tenant-users-access-management': ['Customer Admin', 'Admin'],
  'spatial-hierarchy-property-tree': ['Customer Admin', 'Admin'],
  'batch-qr-code-generator-asset-placards': ['Customer Admin', 'Admin'],
  'resident-portal-defect-submission': ['Raiser'],
  'resident-directory': ['Customer Admin', 'Manager', 'Supervisor', 'Service Desk Agent'],
  'resident-charges': ['Supervisor', 'Manager', 'Customer Admin', 'Finance', 'Service Desk Agent'],
  'service-desk-call-email-intake': ['Service Desk Agent', 'Admin', 'Customer Admin', 'Manager', 'Supervisor'],
  'kanban-triage-sla-board': ['Supervisor', 'Manager', 'Admin', 'Customer Admin'],
  'work-order-packaging-dispatch-board': ['Supervisor', 'Manager', 'Admin', 'Customer Admin'],
  'provider-profile-accreditation-management': ['Provider Admin'],
  'trade-categories-rate-cards': ['Provider Admin', 'Customer Admin', 'Admin'],
  'contractor-quotation-builder': ['Provider Admin'],
  'multi-tier-quotation-approvals-queue': ['Supervisor', 'Manager', 'Customer Admin', 'Admin', 'Finance'],
  'technician-mobile-execution-pwa': ['Worker'],
  'dual-sign-off-resident-verification': ['Worker', 'Raiser'],
  'inspection-checklists-task-template-builder': ['Supervisor', 'Customer Admin', 'Admin'],
  inspections: ['Supervisor', 'Manager', 'Customer Admin', 'Worker'],
  'operational-sla-maintenance-dashboard': ['Manager', 'Supervisor', 'Customer Admin', 'Admin'],
  'provider-benchmark-scorecard': ['Manager', 'Supervisor', 'Customer Admin', 'Admin'],
  'compliance-audit-trail-explorer': ['Auditor', 'Customer Admin', 'Admin'],
  'subscription-tier-seat-licensing': ['Finance', 'Customer Admin'],
  'invoicing-stripe-billing-portal': ['Finance', 'Customer Admin'],
  'tenant-configuration-custom-branding': ['Customer Admin', 'Admin'],
  'assetos-integration': ['Customer Admin', 'Admin'],
};

/**
 * The Xamun Operator console (/admin and everything under it) is a separate area from the tenant pages above:
 * it is not keyed by slug, has its own shell, and only the vendor's own role may open it.
 */
export const OPERATOR_ROLES = ['Xamun Operator'];
export const OPERATOR_HOME = '/admin';
export const isOperatorRole = (role) => OPERATOR_ROLES.some((r) => r.toLowerCase() === String(role ?? '').toLowerCase());

/**
 * An "Admin" that belongs to no customer is the platform administrator: it manages role permissions and
 * integrations for the whole platform and has no customer data to work with. Every other page needs a customer,
 * so for access purposes it is treated as its own role.
 */
export const PLATFORM_ADMIN = 'Platform Admin';
const PLATFORM_ADMIN_SLUGS = ['role-permissions-security-policies', 'system-integrations-webhooks-monitor'];

/** The role to use for page access: the stored role, or Platform Admin for an Admin without a customer. */
export function accessRole(user) {
  if (!user) return undefined;
  return String(user.role).toLowerCase() === 'admin' && !user.tenant ? PLATFORM_ADMIN : user.role;
}

/** True when `role` may open the page at `slug`. Slugs that are not role-gated are open to everyone. */
export function canAccessSlug(role, slug) {
  const roles = ROUTE_ROLES[slug];
  if (!roles) return true;
  if (role === PLATFORM_ADMIN) return PLATFORM_ADMIN_SLUGS.includes(slug);
  const wanted = String(role ?? '').toLowerCase();
  return roles.some((r) => r.toLowerCase() === wanted);
}
